import React, { useState, useEffect, useMemo } from 'react';
import { busRecordsAPI } from '../services/api';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select } from './ui/select';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { AlertDialog } from './ui/alert-dialog';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from './ui/table';
import { useToast } from './ui/toast';
import { 
  Loader2, 
  List, 
  RefreshCw, 
  Edit, 
  Trash2, 
  Bus,
  X,
  Calendar,
  Clock,
  Building2,
  Route,
  Car,
  CreditCard,
  Hash,
  Navigation,
  MapPin,
  Download,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import EnhancedLocationSelector from './EnhancedLocationSelector';

const ITEMS_PER_PAGE = 10;

const BusRecordList = () => {
  const { success, error: showError } = useToast();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  
  // 搜索和筛选
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterRoute, setFilterRoute] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // 'all', 'in-progress', 'completed'
  
  // 分页
  const [currentPage, setCurrentPage] = useState(1);

  const busCompanies = [
    '九巴', '城巴', '嶼巴', '港鐵巴士', '其他'
  ];

  const busTypes = [
    'Enviro500 MMC 12米', 'Enviro500 MMC 12.8米', 'B8L 12米', 'B8L 12.8米',
    'MAN A95 12米', 'MAN A95 12.8米', 'B9TL 10.6米', 'B9TL 12米', 'B9TL 12.8米',
    '比亞迪B12A 12.13m米', '比亞迪B12D 11.97米', '其他'
  ];

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await busRecordsAPI.getAll();
      // 调试：检查API返回的原始数据
      console.log('API返回的原始数据:', response.data);
      if (response.data && response.data.length > 0) {
        console.log('第一条记录的日期:', response.data[0].date, '时间:', response.data[0].time);
      }
      setRecords(response.data);
    } catch (error) {
      setError('載入記錄失敗: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 筛选和搜索
  const filteredRecords = useMemo(() => {
    return records.filter(record => {
      const matchesSearch = !searchQuery || 
        record.route.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.bus_company.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.boarding_location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.alighting_location?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCompany = !filterCompany || record.bus_company === filterCompany;
      const matchesRoute = !filterRoute || record.route.toLowerCase().includes(filterRoute.toLowerCase());
      
      // 状态筛选
      const isInProgress = record.alighting_location === '進行中 - 待填寫';
      const matchesStatus = !filterStatus || 
        (filterStatus === 'in-progress' && isInProgress) ||
        (filterStatus === 'completed' && !isInProgress && record.alighting_location);
      
      return matchesSearch && matchesCompany && matchesRoute && matchesStatus;
    });
  }, [records, searchQuery, filterCompany, filterRoute, filterStatus]);

  // 分页计算
  const totalPages = Math.ceil(filteredRecords.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  // 重置分页当筛选改变时
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterCompany, filterRoute, filterStatus]);

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditFormData({
      date: record.date,
      time: record.time,
      bus_company: record.bus_company,
      route: record.route,
      bus_type: record.bus_type,
      license_plate: record.license_plate || '',
      bus_number: record.bus_number || '',
      boarding_location: record.boarding_location,
      alighting_location: record.alighting_location || ''
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationSelect = (location, type) => {
    setEditFormData(prev => ({
      ...prev,
      [type]: location
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!editFormData.boarding_location || !editFormData.boarding_location.trim()) {
      const errorMessage = '請填寫上車地點';
      showError(errorMessage);
      setError(errorMessage);
      return;
    }

    // 如果下车地点是占位符，必须填写真实地点
    if (!editFormData.alighting_location || !editFormData.alighting_location.trim()) {
      const errorMessage = '請填寫下車地點';
      showError(errorMessage);
      setError(errorMessage);
      return;
    }

    // 不允许保存为占位符（除非是从占位符更新）
    if (editFormData.alighting_location.trim() === '進行中 - 待填寫' && 
        editingRecord?.alighting_location !== '進行中 - 待填寫') {
      const errorMessage = '請填寫真實的下車地點';
      showError(errorMessage);
      setError(errorMessage);
      return;
    }

    try {
      setError('');
      await busRecordsAPI.update(editingRecord.id, editFormData);
      setShowEditModal(false);
      success('記錄已成功更新');
      loadRecords();
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新記錄失敗，請重試';
      showError(errorMessage);
      setError(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    setDeleteConfirm({ open: true, id });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.id) return;
    
      try {
      setError('');
      await busRecordsAPI.delete(deleteConfirm.id);
      success('記錄已成功刪除');
        loadRecords();
      } catch (error) {
      const errorMessage = error.response?.data?.message || '刪除記錄失敗，請重試';
      showError(errorMessage);
      setError(errorMessage);
    }
  };

  // CSV 导出
  const handleExportCSV = () => {
    const headers = ['日期', '時間', '巴士公司', '路線', '車型', '車牌', '巴士編號', '上車地點', '下車地點'];
    const csvContent = [
      headers.join(','),
      ...filteredRecords.map(record => [
        record.date,
        record.time,
        record.bus_company,
        record.route,
        record.bus_type,
        record.license_plate || '',
        record.bus_number || '',
        record.boarding_location || '',
        record.alighting_location || ''
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    // 添加 BOM 以支持中文
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `巴士記錄_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    success('CSV 文件已成功導出');
  };

  const formatDateTime = (date, time) => {
    // 处理日期：如果是 ISO 格式（包含 T），提取日期部分
    let formattedDate = date;
    if (date && typeof date === 'string') {
      // 如果是 ISO 格式，提取日期部分（YYYY-MM-DD）
      if (date.includes('T')) {
        formattedDate = date.split('T')[0];
      } else if (date.includes(' ')) {
        // 如果包含空格，取第一部分
        formattedDate = date.split(' ')[0];
      }
    }
    
    // 处理时间：如果时间包含日期部分，只取时间部分
    let formattedTime = time;
    if (time && typeof time === 'string') {
      // 如果时间包含空格或 T，可能是日期时间格式
      if (time.includes('T')) {
        // ISO 格式，提取时间部分
        const timePart = time.split('T')[1];
        formattedTime = timePart ? timePart.split('.')[0].substring(0, 5) : time.substring(0, 5);
      } else if (time.includes(' ')) {
        // 如果包含空格，取时间部分（最后一个空格后的部分）
        const parts = time.split(' ');
        formattedTime = parts.length > 1 ? parts[parts.length - 1].substring(0, 5) : time.substring(0, 5);
      } else {
        // 只取前5个字符（HH:MM）
        formattedTime = time.substring(0, 5);
      }
    }
    
    // 如果日期或时间为空，返回默认值
    if (!formattedDate) formattedDate = '-';
    if (!formattedTime) formattedTime = '-';
    
    return `${formattedDate} ${formattedTime}`;
  };

  if (loading) {
    return (
      <div
        className="container mx-auto px-2 sm:px-4 py-2 sm:py-4 md:py-6 max-w-6xl flex items-stretch"
        style={{ minHeight: 'calc(100vh - 80px)', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <Card className="shadow-lg w-full flex flex-col">
          <CardContent className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">載入中...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="container mx-auto px-2 sm:px-4 py-2 sm:py-4 md:py-6 max-w-6xl flex items-stretch"
      style={{ minHeight: 'calc(100vh - 80px)', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <Card className="shadow-lg w-full flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <List className="h-6 w-6" />
                巴士記錄列表
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                type="button"
                variant="outline" 
                onClick={handleExportCSV} 
                disabled={filteredRecords.length === 0}
                className="min-h-[44px]"
              >
                <Download className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">導出 CSV</span>
                <span className="sm:hidden">導出</span>
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={loadRecords} 
                disabled={loading}
                className="min-h-[44px]"
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
                <span className="hidden sm:inline">刷新</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* 搜索和筛选 */}
          <div className="mb-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label htmlFor="search" className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  搜索
                </Label>
                <Input
                  id="search"
                  type="text"
                  placeholder="搜索路線、公司、地點..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="filter-company" className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  巴士公司
                </Label>
                <Select
                  id="filter-company"
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                >
                  <option value="">全部</option>
                  {busCompanies.map(company => (
                    <option key={company} value={company}>{company}</option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="filter-route">路線</Label>
                <Input
                  id="filter-route"
                  type="text"
                  placeholder="篩選路線..."
                  value={filterRoute}
                  onChange={(e) => setFilterRoute(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="filter-status" className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                  狀態
                </Label>
                <Select
                  id="filter-status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">全部</option>
                  <option value="in-progress">進行中</option>
                  <option value="completed">已完成</option>
                </Select>
              </div>
            </div>
            {filteredRecords.length !== records.length && (
              <p className="text-sm text-muted-foreground">
                顯示 {filteredRecords.length} / {records.length} 條記錄
              </p>
            )}
          </div>

          {filteredRecords.length === 0 ? (
            <div className="text-center py-12">
              <Bus className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h5 className="text-lg font-medium text-muted-foreground mb-2">
                {records.length === 0 ? '暫無記錄' : '未找到匹配的記錄'}
              </h5>
              <p className="text-sm text-muted-foreground">
                {records.length === 0 ? '開始記錄您的巴士行程吧！' : '請嘗試調整搜索條件'}
              </p>
                </div>
              ) : (
            <>
              <div className="rounded-md border overflow-hidden" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px]">日期時間</TableHead>
                        <TableHead className="min-w-[80px]">巴士公司</TableHead>
                        <TableHead className="min-w-[60px]">路線</TableHead>
                        <TableHead className="min-w-[120px] hidden sm:table-cell">車型</TableHead>
                        <TableHead className="min-w-[100px]">上車地點</TableHead>
                        <TableHead className="min-w-[100px]">下車地點</TableHead>
                        <TableHead className="text-right min-w-[100px]">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                    {paginatedRecords.map(record => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">
                          {formatDateTime(record.date, record.time)}
                        </TableCell>
                        <TableCell>{record.bus_company}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary font-medium">
                            {record.route}
                          </span>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{record.bus_type}</TableCell>
                        <TableCell>{record.boarding_location || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {record.alighting_location === '進行中 - 待填寫' ? (
                              <>
                                <span className="text-muted-foreground italic">進行中</span>
                                <AlertCircle className="h-4 w-4 text-amber-500" title="待完成" />
                              </>
                            ) : (
                              <>
                                <span>{record.alighting_location || '-'}</span>
                                {record.alighting_location && (
                                  <CheckCircle2 className="h-4 w-4 text-green-500" title="已完成" />
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1 sm:gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(record)}
                              className="min-h-[44px] min-w-[44px]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(record.id)}
                              className="text-destructive hover:text-destructive min-h-[44px] min-w-[44px]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* 分页 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    顯示第 {startIndex + 1} - {Math.min(endIndex, filteredRecords.length)} 條，共 {filteredRecords.length} 條記錄
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      上一頁
                    </Button>
                    <span className="text-xs sm:text-sm text-muted-foreground px-2">
                      第 {currentPage} / {totalPages} 頁
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="min-h-[44px]"
                    >
                      <span className="hidden sm:inline">下一頁</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
          </Card>

      {/* 删除确认对话框 */}
      <AlertDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, id: null })}
        title="確認刪除"
        description="確定要刪除此記錄嗎？此操作無法復原。"
        confirmText="刪除"
        cancelText="取消"
        onConfirm={confirmDelete}
        variant="destructive"
      />

      {/* Edit Dialog */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>編輯記錄</DialogTitle>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowEditModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="edit-date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  日期 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-date"
                    type="date"
                    name="date"
                    value={editFormData.date}
                    onChange={handleEditChange}
                    required
                  />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-time" className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  時間 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-time"
                    type="time"
                    name="time"
                    value={editFormData.time}
                    onChange={handleEditChange}
                    required
                  />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="edit-bus-company" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  巴士公司 <span className="text-destructive">*</span>
                </Label>
                <Select
                  id="edit-bus-company"
                    name="bus_company"
                    value={editFormData.bus_company}
                    onChange={handleEditChange}
                    required
                  >
                    {busCompanies.map(company => (
                      <option key={company} value={company}>{company}</option>
                    ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-route" className="flex items-center gap-2">
                  <Route className="h-4 w-4 text-muted-foreground" />
                  路線 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-route"
                    type="text"
                    name="route"
                    value={editFormData.route}
                    onChange={handleEditChange}
                    required
                  />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="edit-bus-type" className="flex items-center gap-2">
                  <Car className="h-4 w-4 text-muted-foreground" />
                  車型 <span className="text-destructive">*</span>
                </Label>
                <Select
                  id="edit-bus-type"
                    name="bus_type"
                    value={editFormData.bus_type}
                    onChange={handleEditChange}
                    required
                  >
                    {busTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-license-plate" className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  車牌
                </Label>
                <Input
                  id="edit-license-plate"
                    type="text"
                    name="license_plate"
                    value={editFormData.license_plate}
                    onChange={handleEditChange}
                  />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-bus-number" className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                巴士編號
              </Label>
              <Input
                id="edit-bus-number"
                type="text"
                name="bus_number"
                value={editFormData.bus_number}
                onChange={handleEditChange}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Navigation className="h-4 w-4 text-muted-foreground" />
                上車地點 <span className="text-destructive">*</span>
              </Label>
              <EnhancedLocationSelector
                value={editFormData.boarding_location}
                onSelect={(location) => handleLocationSelect(location, 'boarding_location')}
                placeholder="請選擇或輸入上車地點"
                showMap={true}
                showSearch={true}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                下車地點 <span className="text-destructive">*</span>
              </Label>
              <EnhancedLocationSelector
                value={editFormData.alighting_location}
                onSelect={(location) => handleLocationSelect(location, 'alighting_location')}
                placeholder="請選擇或輸入下車地點"
                showMap={true}
                showSearch={true}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditModal(false)}
              >
              取消
            </Button>
              <Button type="submit">
              保存更改
            </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusRecordList;
